import z from 'zod'

export const millsheetSortConfigSchema = z.object({
  // 「メーカー」「規格」「形状」の並び替え順をカスタマイズ設定できる
  manufacturerName: z.string().array().nullish(),
  steelType: z.string().array().nullish(),
  sectionType: z.string().array().nullish(),
})

export type MillsheetSortConfig = z.infer<typeof millsheetSortConfigSchema>

export function sortStringsByConfig(values: string[], configOrder: string[] | null | undefined): string[] {
  if (!configOrder || configOrder.length === 0) {
    return [...values].sort((a, b) => a.localeCompare(b, 'ja', { numeric: true, sensitivity: 'base' }))
  }
  return [...values].sort((a, b) => {
    const aIdx = configOrder.indexOf(a)
    const bIdx = configOrder.indexOf(b)
    const aEff = aIdx === -1 ? configOrder.length : aIdx
    const bEff = bIdx === -1 ? configOrder.length : bIdx
    if (aEff !== bEff) {
      return aEff - bEff
    }
    // 両方とも未設定の場合は localeCompare フォールバック
    return a.localeCompare(b, 'ja', { numeric: true, sensitivity: 'base' })
  })
}

type Value = string | null | number

export type SortDirection = 'asc' | 'desc'
export type SortKeySpec = { key: string; direction: SortDirection }
// 従来の string[]（昇順のみ）の呼び出しを壊さないため、キー名だけの指定も受け付ける
export type SortKeyInput = string | SortKeySpec

export function normalizeSortKeys(sortKeys: readonly SortKeyInput[]): SortKeySpec[] {
  return sortKeys.map((x) => (typeof x === 'string' ? { key: x, direction: 'asc' } : x))
}

// 降順は比較結果の符号反転で実装する。組織設定の登録順・未登録値（末尾）もそのまま反転させるため
export function applySortDirection(result: number, direction: SortDirection): number {
  return direction === 'desc' ? -result : result
}

export function sortMillsheetItems<Item extends Record<string, Value>>(
  items: Item[],
  sortConfig: MillsheetSortConfig,
  sortKeys: readonly SortKeyInput[],
): Item[] {
  if (sortKeys.length === 0) {
    return items
  }
  const specs = normalizeSortKeys(sortKeys)
  // 汎用的に使えるようにするため、Record型のItemを、keysでソートする
  return [...items].sort((a, b) => {
    for (const { key, direction } of specs) {
      const aValue = a[key] ?? ''
      const bValue = b[key] ?? ''
      if (aValue !== bValue) {
        const result = compareMillsheetItemValueByConfig(aValue, bValue, sortConfig, key)
        if (result !== 0) {
          return applySortDirection(result, direction)
        }
      }
    }
    return 0
  })
}

export function compareMillsheetItemValueByConfig(
  aValue: Value,
  bValue: Value,
  sortConfig: MillsheetSortConfig,
  sortKey: string,
) {
  const sortConfigItem = sortConfig[sortKey as keyof MillsheetSortConfig] ?? []
  if (sortConfigItem.length > 0 && typeof aValue === 'string' && typeof bValue === 'string') {
    const aFoundIndex = sortConfigItem.indexOf(aValue)
    const bFoundIndex = sortConfigItem.indexOf(bValue)
    // 含まれていない値は、最後扱いにする
    const aIndex = aFoundIndex === -1 ? sortConfigItem.length : aFoundIndex
    const bIndex = bFoundIndex === -1 ? sortConfigItem.length : bFoundIndex
    if (aIndex === bIndex) {
      // 両方とも未登録の値は、登録済みの後ろで自然順の昇順にする（集計表の仕様と揃える）
      return aValue.localeCompare(bValue, 'ja', { numeric: true, sensitivity: 'base' })
    }
    return aIndex - bIndex
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue
  }

  if (aValue === null && bValue === null) {
    return 0
  }
  if (aValue === null) {
    return 1
  }
  if (bValue === null) {
    return -1
  }

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return aValue.localeCompare(bValue, 'ja', { numeric: true, sensitivity: 'base' })
  }

  return 0
}
